<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { useNotification } from '@kyvg/vue3-notification'
import { useMatchMedia } from '@/utils'
import SiteAdminService from '@/services/SiteAdminService'
import { useSiteDraft } from '@/composables/useSiteDraft'
import { urlError } from '@/Utils/siteUrl'
import { HEX_COLOR, COLOR_FIELDS, seedColor } from '@/Utils/siteBranding'
import Spinner from '@/components/ui/Spinner.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import FormInput from '@/components/Atoms/FormInput.vue'
import FormSelect from '@/components/Atoms/FormSelect.vue'
import FormSwitch from '@/components/Atoms/FormSwitch.vue'
import TabGroup from '@/components/Atoms/TabGroup.vue'
import TabItem from '@/components/Atoms/TabItem.vue'
import Alert from '@/components/Atoms/Alert.vue'
import SiteImageField from '@/components/Sites/SiteImageField.vue'
import SiteFontSelect from '@/components/Sites/SiteFontSelect.vue'
import SiteDisplaySettings from '@/components/Sites/SiteDisplaySettings.vue'
import SitePreviewPane from '@/components/Sites/SitePreviewPane.vue'
import BroadcastTextsCard from '@/components/Sites/Broadcast/BroadcastTextsCard.vue'
import SiteAuthTextsCard from '@/components/Sites/SiteAuthTextsCard.vue'
import { normalizeTexts, normalizeAudience, BROADCAST_TEXT_MAX } from '@/components/Sites/broadcastAudience'
import { normalizeAuthTexts, AUTH_TEXT_MAX, LEGACY_AUTH_TEXT_KEYS } from '@/components/Sites/siteAuthTexts'
import { DISPLAY_CARDS, DISPLAY_PRESETS, effectiveDisplayValues, displayChanges, displayErrors, isWritable, sameValue, clone } from '@/components/Sites/siteDisplayOptions'
import { trackSites, SITES_EVENTS } from '@/lib/tracking/sites'
import { BROADCAST_THEME_PRESETS, TEMPLATE_BROADCAST, siteTemplate, templateMeta } from '@/components/Sites/siteTemplates'
import IconBase from '@/components/icon/IconBase.vue'
import IconBin from '@/components/icon/IconBin.vue'
import IconPlus from '@/components/icon/IconPlus.vue'
import IconEyeOutline from '@/components/icon/IconEyeOutline.vue'

// Runtime settings (site-admin `GET/PATCH /settings`) split into sub-tabs:
// Branding · Layout · Navigation & sections · Player · Texts · Access &
// accounts · Footer, with a live preview pane beside them from `xl` up.
// Changes are tracked per sub-tab (only keys that differ from the loaded
// values are PATCHed) and saved to the runtime *draft* (`?target=draft`,
// sites-draft-publish-contract.md); a runtime without drafts publishes at
// once. Unknown keys are a 400 on the runtime, so display keys are gated on
// GET's `writableKeys`.
const props = defineProps({
	siteId: {
		type: String,
		required: true,
	},
	site: {
		type: Object,
		required: true,
	},
	/** Active sub-tab (route param `settings/theme/:section?`). */
	section: {
		type: String,
		default: '',
	},
})
const { notify } = useNotification()
const router = useRouter()
const draft = useSiteDraft(() => props.siteId)
// The preview sits beside the form only when the *content column* can hold a
// 480 px pane next to a usable form (the app sidebar and the site rail take
// ~590 px of the viewport: the column is 694 px at 1280, 780 at 1366, 854 at
// 1440, so a viewport breakpoint alone would squeeze the form to nothing).
// Narrower → a full-width collapsible preview section under the form, where
// the scaled 16:9 frame is far larger than a side column would allow.
const SIDE_BY_SIDE_MIN_PX = 900
const PREVIEW_PREF_KEY = 'castr.sites.themePreview'
const rootEl = ref(null)
const rootWidth = ref(0)
const sideBySide = computed(() => rootWidth.value >= SIDE_BY_SIDE_MIN_PX)
const readPreviewPref = () => {
	try { return window.localStorage.getItem(PREVIEW_PREF_KEY) !== '0' } catch { return true }
}
const previewShown = ref(readPreviewPref())
const togglePreview = () => {
	previewShown.value = !previewShown.value
	try { window.localStorage.setItem(PREVIEW_PREF_KEY, previewShown.value ? '1' : '0') } catch { /* private mode */ }
}
let resizeObserver = null
const mdUp = useMatchMedia('(min-width: 768px)')

// `templates` = home-page templates that use the section; the broadcast
// template (sites-broadcast-template-contract.md §7) has no hero, categories
// or catalog texts, so Layout / Navigation & sections / Texts are dropped and
// its own Settings › Broadcast page takes over.
const SECTIONS = [
	{ id: 'branding', label: 'Branding', description: 'Logo, favicon, colours and font.' },
	{ id: 'layout', label: 'Layout', description: 'Presets and how the home page, categories and cards are arranged.', card: 'layout', templates: ['catalog'] },
	{ id: 'sections', label: 'Navigation & sections', description: 'Which parts of the site viewers see and where they land.', card: 'sections', templates: ['catalog'] },
	{ id: 'player', label: 'Player', description: 'Playback behaviour, badges and sharing.', card: 'player' },
	{ id: 'texts', label: 'Texts', description: 'Labels and calls to action viewers read.', card: 'texts', templates: ['catalog'] },
	// Broadcast (v0.19): every string of the Broadcast page (`broadcastTexts`); shown when the runtime lists the key.
	{ id: 'texts', label: 'Texts', description: 'Every label on your Broadcast page — badges, countdown, registration form, giving and sponsor.', templates: ['broadcast'], key: 'broadcastTexts' },
	{ id: 'access', label: 'Access & accounts', description: 'Registration, legal pages and support contact. Who can watch is set per content item.' },
	{ id: 'footer', label: 'Footer', description: 'Footer text, your own links and social / app-store links.' },
]
// castr's `site.template` until the runtime answers (`GET /settings → template` on v0.15+).
const runtimeTemplate = ref(null)
const template = computed(() => runtimeTemplate.value || siteTemplate(props.site))
const isBroadcast = computed(() => template.value === TEMPLATE_BROADCAST)
const templateLabel = computed(() => templateMeta(template.value).name)
// Keys the template does not read on the home page (`templateIgnoredKeys`): greyed, still saved.
const templateIgnoredKeys = ref([])
const sections = computed(() => SECTIONS.filter((s) => (!s.templates || s.templates.includes(template.value)) && (!s.key || (Array.isArray(writableKeys.value) && writableKeys.value.includes(s.key)))))
const section = computed(() => (sections.value.some((s) => s.id === props.section) ? props.section : 'branding'))
const sectionMeta = computed(() => sections.value.find((s) => s.id === section.value))
const goSection = (id) => router.replace({ name: 'SiteTheme', params: { siteId: props.siteId, section: id } })

const loading = ref(true)
const loadError = ref(null)
const saving = ref(false)
// `settings` = the values being edited against: published ⊕ draft.
const settings = ref({})
const seeded = ref([])
const writableKeys = ref(null)
const categories = ref([])
// Site pages (Content › Pages) offered as Terms / Privacy targets.
const pages = ref([])
// Display cards (siteDisplayOptions.js): `displayBase` = what the runtime
// has now, `displayDraft` = what the owner is editing; only the difference is PATCHed.
const displayBase = ref(effectiveDisplayValues({}))
const displayDraft = ref(clone(displayBase.value))
const syncDisplay = () => {
	displayBase.value = effectiveDisplayValues(settings.value)
	displayDraft.value = clone(displayBase.value)
}

const HEX = HEX_COLOR
// Footer link platforms the runtime renders (constants/socialPlatforms).
const PLATFORMS = ['facebook', 'instagram', 'x', 'linkedin', 'youtube', 'tiktok', 'playstore', 'appstore', 'roku', 'firetv', 'samsung', 'lg']
const PLATFORM_LABELS = { x: 'X (Twitter)', playstore: 'Google Play', appstore: 'App Store', firetv: 'Fire TV', lg: 'LG', linkedin: 'LinkedIn', youtube: 'YouTube', tiktok: 'TikTok' }
const platformLabel = (p) => PLATFORM_LABELS[p] || p.charAt(0).toUpperCase() + p.slice(1)
const platformOptions = PLATFORMS.map((p) => ({ value: p, text: platformLabel(p) }))

// The site name is not part of this form: Settings › General is its only
// source (castr pushes it to the runtime's `websiteName` and publishes).
const emptyForm = () => ({
	websiteLogo: '',
	websiteFavicon: '',
	fontFamily: '',
	colors: { primary: '', background: '', text: '' },
	websiteFooterText: '',
	footerLinks: [],
	socialLinks: [],
	isSocialPublic: true,
	registrationEnabled: true,
	termsUrl: '',
	privacyUrl: '',
	supportEmail: '',
	broadcastTexts: {},
	authTexts: {},
})
const form = ref(emptyForm())
const audience = computed(() => normalizeAudience(settings.value.broadcastAudience))
const broadcastTextsWritable = computed(() => isBroadcast.value && Array.isArray(writableKeys.value) && writableKeys.value.includes('broadcastTexts'))
// v0.20: sign-in / sign-up texts on both templates; they take over the older
// single-label controls (header and form buttons) in the catalog Texts card.
const authTextsWritable = computed(() => Array.isArray(writableKeys.value) && writableKeys.value.includes('authTexts'))
const displayWritableKeys = computed(() => (authTextsWritable.value ? writableKeys.value.filter((k) => !LEGACY_AUTH_TEXT_KEYS.includes(k)) : writableKeys.value))

// Legal & support (round 6): runtime keys termsUrl / privacyUrl are offered
// only when the runtime advertises them in `writableKeys` (older runtimes
// answer 400 for unknown keys); supportEmail moved here from Texts.
const LEGAL_FIELDS = [
	{ key: 'termsUrl', label: 'Terms of Service URL', help: 'A page on this site or an https:// address (http:// is not accepted).' },
	{ key: 'privacyUrl', label: 'Privacy Policy URL', help: 'A page on this site or an https:// address (http:// is not accepted).' },
]
const legalKeyWritable = (key) => Array.isArray(writableKeys.value) && writableKeys.value.includes(key)
const legalSupported = computed(() => LEGAL_FIELDS.some((f) => legalKeyWritable(f.key)))
const supportEmailWritable = computed(() => isWritable('supportEmail', writableKeys.value))
// Hosted pages link by `/`-relative path (works on every domain of the site); external pages by their URL.
const pageLinkOptions = computed(() => [
	{ value: '', text: 'Use a site page…' },
	...pages.value.filter((pg) => pg.published !== false && String(pg.url || '').trim()).map((pg) => ({ value: pg.external ? String(pg.url).trim() : `/${String(pg.url).trim().replace(/^\/+/, '')}`, text: pg.title || pg.url })),
])
const pickPage = (key, value) => { if (value) form.value[key] = value }

const colorFields = COLOR_FIELDS
// Saved value → castr KV seed (`site.theme.*Color`) → runtime default (Utils/siteBranding.js).
const defaultColor = (key) => seedColor(key, props.site)
const defaultColors = computed(() => Object.fromEntries(colorFields.map((f) => [f.key, defaultColor(f.key)])))
const isDefaultColor = (key) => String(form.value.colors[key] || '').toLowerCase() === defaultColors.value[key].toLowerCase()
const allColorsDefault = computed(() => colorFields.every((f) => isDefaultColor(f.key)))
const resetColor = (key) => { form.value.colors[key] = defaultColors.value[key] }
const resetAllColors = () => colorFields.forEach((f) => resetColor(f.key))
// Broadcast colour presets (contract §2.3): plain `colors` values, applied on save like any colour edit.
const sameHex = (a, b) => String(a || '').toLowerCase() === String(b || '').toLowerCase()
const broadcastPresets = computed(() => BROADCAST_THEME_PRESETS.map((p) => ({ ...p, active: colorFields.every((f) => sameHex(form.value.colors[f.key], p.colors[f.key])) })))
const applyBroadcastPreset = (preset) => { form.value.colors = { ...form.value.colors, ...preset.colors } }

// Recommended assets (#4). The uploader takes raster images up to 10 MB; the
// runtime refuses SVG (XSS vector), so the guidance never suggests it.
const LOGO_HINT = 'PNG or WebP with transparent background, at least 400×100 px, ideally under 1 MB (max 10 MB)'
const FAVICON_HINT = 'PNG 512×512 px (max 10 MB)'
const ONE_MB = 1024 * 1024

const savedColor = (key) => settings.value.colors?.[key] || (key === 'text' ? settings.value.colors?.textColor : '') || ''
const normFooterLinks = (list) => (Array.isArray(list) ? list : []).filter((l) => String(l.label || '').trim() && String(l.url || '').trim()).map((l) => ({ label: String(l.label).trim(), url: String(l.url).trim() }))
const normSocialLinks = (list) => (Array.isArray(list) ? list : []).filter((l) => String(l.url || '').trim()).map((l) => ({ platform: l.platform || 'x', url: String(l.url).trim(), color: l.color || '#73787E' }))

const fillForm = () => {
	const s = settings.value
	form.value = {
		websiteLogo: s.websiteLogo || '',
		websiteFavicon: s.websiteFavicon || '',
		fontFamily: s.fontFamily || '',
		// Always show a swatch: saved value → KV theme seed → runtime default.
		colors: {
			primary: savedColor('primary') || defaultColor('primary'),
			background: savedColor('background') || defaultColor('background'),
			text: savedColor('text') || defaultColor('text'),
		},
		websiteFooterText: s.websiteFooterText || '',
		footerLinks: Array.isArray(s.footerLinks) ? s.footerLinks.map((l) => ({ label: l.label || '', url: l.url || '' })) : [],
		socialLinks: Array.isArray(s.socialLinks) ? s.socialLinks.map((l) => ({ platform: l.platform || 'x', url: l.url || '', color: l.color || '#73787E' })) : [],
		isSocialPublic: s.isSocialPublic !== false,
		registrationEnabled: s.registrationEnabled !== false,
		termsUrl: s.termsUrl || '',
		privacyUrl: s.privacyUrl || '',
		supportEmail: s.supportEmail || '',
		broadcastTexts: normalizeTexts(s.broadcastTexts),
		authTexts: normalizeAuthTexts(s.authTexts),
	}
	syncDisplay()
}

const load = async () => {
	try {
		const [res, categoryRes, pagesRes] = await Promise.all([
			SiteAdminService.getSettings(props.siteId),
			SiteAdminService.listCategories(props.siteId).catch(() => ({ items: [] })),
			SiteAdminService.listPages(props.siteId).catch(() => ({ items: [] })),
		])
		// Edit against published ⊕ draft so the form shows what the preview shows.
		settings.value = { ...(res?.settings || {}), ...(res?.draft && typeof res.draft === 'object' ? res.draft : {}) }
		seeded.value = res?.seeded || []
		writableKeys.value = Array.isArray(res?.writableKeys) ? res.writableKeys : null
		runtimeTemplate.value = res?.template === TEMPLATE_BROADCAST || res?.template === 'catalog' ? res.template : null
		templateIgnoredKeys.value = Array.isArray(res?.templateIgnoredKeys) ? res.templateIgnoredKeys : []
		categories.value = categoryRes?.items || []
		pages.value = pagesRes?.items || []
		draft.applySettingsResponse(res)
		if (typeof res?.draftCount !== 'number' && !res?.draft) draft.state.value.draftCount = null
		fillForm()
		loadError.value = null
	} catch (err) {
		loadError.value = err
	}
}
onMounted(async () => { loading.value = true; await load(); loading.value = false })

// --- validation ------------------------------------------------------------------
const colorErrors = computed(() => Object.fromEntries(colorFields.filter((f) => form.value.colors[f.key] && !HEX.test(form.value.colors[f.key])).map((f) => [f.key, 'Use a hex colour like #4f7cff'])))
const linkErrors = computed(() => form.value.socialLinks.map((l) => urlError(l.url)))
// v0.3: ≤ 20 links, label 1–80 chars, http(s) URL or /-relative path.
const MAX_FOOTER_LINKS = 20
const footerLinkErrors = computed(() => form.value.footerLinks.map((l) => {
	const urlErr = urlError(l.url, { allowRelative: true })
	if (urlErr) return urlErr
	if (l.label && l.label.trim().length > 80) return 'Label must be 80 characters or fewer'
	if (l.url.trim() && !l.label.trim()) return 'Add a label for this link'
	if (l.label.trim() && !l.url.trim()) return 'Add a URL for this link'
	return null
}))
const imageErrors = computed(() => ({ logo: urlError(form.value.websiteLogo), favicon: urlError(form.value.websiteFavicon) }))
const fontSelect = ref(null)
const fontError = computed(() => fontSelect.value?.customError || null)
const displayErrs = computed(() => displayErrors(displayDraft.value, { urlError }))
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
// Runtime `isLegalUrl`: an https:// URL (plain http:// is refused — the
// runtime is tightening the same way) or one site page slug (`/<slug>`, no
// nested paths).
const PAGE_PATH = /^\/[a-z0-9][a-z0-9-]*$/i
const legalErrors = computed(() => {
	const e = {}
	for (const f of LEGAL_FIELDS) {
		if (!legalKeyWritable(f.key)) continue
		const value = String(form.value[f.key] || '').trim()
		const err = urlError(value, { allowRelative: true })
		if (err) e[f.key] = err
		else if (/^http:\/\//i.test(value)) e[f.key] = 'Use an https:// address — plain http:// links are not accepted'
		else if (value.startsWith('/') && !PAGE_PATH.test(value)) e[f.key] = 'Use a site page path like /terms (one segment) or a full https:// URL'
	}
	const email = String(form.value.supportEmail || '').trim()
	if (supportEmailWritable.value && email && !EMAIL.test(email)) e.supportEmail = 'Enter a valid email address'
	return e
})
const sectionErrors = computed(() => ({
	branding: Object.keys(colorErrors.value).length > 0 || Boolean(imageErrors.value.logo || imageErrors.value.favicon || fontError.value),
	layout: cardKeys('layout').some((k) => displayErrs.value[k]),
	sections: cardKeys('sections').some((k) => displayErrs.value[k]),
	player: cardKeys('player').some((k) => displayErrs.value[k]),
	texts: (broadcastTextsWritable.value
		? Object.values(form.value.broadcastTexts || {}).some((v) => String(v || '').trim().length > BROADCAST_TEXT_MAX)
		: cardKeys('texts').some((k) => displayErrs.value[k]))
		|| (authTextsWritable.value && Object.values(form.value.authTexts || {}).some((v) => String(v || '').trim().length > AUTH_TEXT_MAX)),
	access: Object.keys(legalErrors.value).length > 0,
	footer: linkErrors.value.some(Boolean) || footerLinkErrors.value.some(Boolean),
}))
const valid = computed(() => !Object.values(sectionErrors.value).some(Boolean))

// --- per-section changes (only keys that differ from the loaded values) ------------
const norm = (v) => (v === undefined || v === null ? '' : v)
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b)
function cardKeys(cardId) {
	const card = DISPLAY_CARDS.find((c) => c.id === cardId)
	return card ? card.controls.map((c) => c.key).filter((k) => isWritable(k, writableKeys.value)) : []
}
const displayPatch = computed(() => displayChanges(displayDraft.value, displayBase.value, writableKeys.value))
const cardPatch = (cardId) => Object.fromEntries(Object.entries(displayPatch.value).filter(([k]) => cardKeys(cardId).includes(k)))

const brandingPatch = computed(() => {
	const s = settings.value
	const f = form.value
	const p = {}
	for (const key of ['websiteLogo', 'websiteFavicon', 'fontFamily']) {
		if (String(f[key] || '').trim() !== String(norm(s[key])).trim()) p[key] = String(f[key] || '').trim()
	}
	const changedColors = Object.fromEntries(colorFields
		.filter((fld) => String(f.colors[fld.key] || '').toLowerCase() !== String(savedColor(fld.key) || defaultColor(fld.key)).toLowerCase())
		.map((fld) => [fld.key, f.colors[fld.key]]))
	if (Object.keys(changedColors).length) p.colors = { ...(s.colors || {}), ...changedColors }
	return p
})
const accessPatch = computed(() => {
	const s = settings.value
	const p = {}
	if (form.value.registrationEnabled !== (s.registrationEnabled !== false)) p.registrationEnabled = form.value.registrationEnabled
	for (const f of LEGAL_FIELDS) {
		if (!legalKeyWritable(f.key)) continue
		const v = String(form.value[f.key] || '').trim()
		if (v !== String(norm(s[f.key])).trim()) p[f.key] = v
	}
	if (supportEmailWritable.value) {
		const email = String(form.value.supportEmail || '').trim()
		if (email !== String(norm(s.supportEmail)).trim()) p.supportEmail = email
	}
	return p
})
const footerPatch = computed(() => {
	const s = settings.value
	const f = form.value
	const p = {}
	if (f.websiteFooterText !== norm(s.websiteFooterText)) p.websiteFooterText = f.websiteFooterText
	if (!same(normFooterLinks(f.footerLinks), normFooterLinks(s.footerLinks))) p.footerLinks = normFooterLinks(f.footerLinks)
	if (!same(normSocialLinks(f.socialLinks), normSocialLinks(s.socialLinks))) p.socialLinks = normSocialLinks(f.socialLinks)
	if (f.isSocialPublic !== (s.isSocialPublic !== false)) p.isSocialPublic = f.isSocialPublic
	return p
})
// `{}` clears every override on the runtime.
const broadcastTextsPatch = computed(() => {
	const next = normalizeTexts(form.value.broadcastTexts)
	return same(next, normalizeTexts(settings.value.broadcastTexts)) ? {} : { broadcastTexts: next }
})
const authTextsPatch = computed(() => {
	if (!authTextsWritable.value) return {}
	const next = normalizeAuthTexts(form.value.authTexts)
	return same(next, normalizeAuthTexts(settings.value.authTexts)) ? {} : { authTexts: next }
})
const patches = computed(() => ({
	branding: brandingPatch.value,
	layout: cardPatch('layout'),
	sections: cardPatch('sections'),
	player: cardPatch('player'),
	texts: { ...(broadcastTextsWritable.value ? broadcastTextsPatch.value : cardPatch('texts')), ...authTextsPatch.value },
	access: accessPatch.value,
	footer: footerPatch.value,
}))
const changeCount = (id) => Object.keys(patches.value[id] || {}).length
const fullPatch = computed(() => Object.assign({}, ...Object.values(patches.value)))
const unsavedCount = computed(() => Object.keys(fullPatch.value).length)
const dirtySections = computed(() => sections.value.filter((s) => changeCount(s.id) > 0))
const displayChangeCount = computed(() => Object.keys(displayPatch.value).length)

// --- draft / publish state ---------------------------------------------------------
const draftSupported = computed(() => draft.draftSupported.value)
const unpublishedCount = computed(() => draft.draftCount.value)
const publishing = computed(() => draft.state.value.publishing)
const previewVersion = computed(() => draft.state.value.version)
const saveLabel = computed(() => (draftSupported.value ? 'Save draft' : 'Save settings'))

const discardUnsaved = () => fillForm()

const addFooterLink = () => form.value.footerLinks.push({ label: '', url: '' })
const removeFooterLink = (i) => form.value.footerLinks.splice(i, 1)
const addLink = () => {
	const used = new Set(form.value.socialLinks.map((l) => l.platform))
	const platform = PLATFORMS.find((p) => !used.has(p)) || 'x'
	form.value.socialLinks.push({ platform, url: '', color: '#73787E' })
}
const removeLink = (i) => form.value.socialLinks.splice(i, 1)

/** The colour or layout preset a save lands on, when the save itself changed that preset's keys. */
const savedPreset = (patch) => {
	if (isBroadcast.value && 'colors' in patch) {
		const colors = broadcastPresets.value.find((p) => p.active)
		if (colors) return colors.id
	}
	const layout = DISPLAY_PRESETS.find((p) => {
		const keys = Object.keys(p.values).filter((k) => isWritable(k, writableKeys.value))
		return keys.length > 0 && keys.some((k) => k in patch) && keys.every((k) => sameValue(displayDraft.value[k], p.values[k]))
	})
	return layout?.id || null
}

const save = async () => {
	if (!valid.value || saving.value || !unsavedCount.value) return
	saving.value = true
	const patch = { ...fullPatch.value }
	const preset = savedPreset(patch)
	try {
		const res = await SiteAdminService.updateSettings(props.siteId, patch, { target: 'draft' })
		if (preset) trackSites(SITES_EVENTS.THEME_PRESET_APPLIED, props.siteId, { preset, template: template.value })
		// The draft answer echoes the published values; our editing baseline is published ⊕ draft.
		settings.value = { ...settings.value, ...patch }
		if (Array.isArray(res?.writableKeys)) writableKeys.value = res.writableKeys
		draft.noteDraftSaved(res)
		fillForm()
		const n = Object.keys(patch).length
		notify({
			group: 'success',
			text: draft.draftSupported.value
				? `${n} ${n === 1 ? 'change' : 'changes'} saved to the draft. Publish when you are ready.`
				: 'Settings saved.',
		})
	} catch (err) {
		notify({ group: 'error', text: err.message || 'Could not save settings.' })
	}
	saving.value = false
}
const publish = () => draft.publish().catch(() => null)

// Leaving the browser tab with unsaved changes warns; sub-tab switches keep the state.
const beforeUnload = (e) => { if (unsavedCount.value) { e.preventDefault(); e.returnValue = '' } }
onMounted(() => {
	window.addEventListener('beforeunload', beforeUnload)
	if (typeof ResizeObserver !== 'undefined' && rootEl.value) {
		resizeObserver = new ResizeObserver((entries) => { rootWidth.value = entries[0]?.contentRect?.width || 0 })
		resizeObserver.observe(rootEl.value)
	} else if (rootEl.value) {
		rootWidth.value = rootEl.value.getBoundingClientRect().width
	}
})
onBeforeUnmount(() => {
	window.removeEventListener('beforeunload', beforeUnload)
	resizeObserver?.disconnect()
})
</script>

<template>
	<div ref="rootEl" data-testid="site-theme" :data-template="template" :data-preview-layout="sideBySide ? 'side' : (mdUp && previewShown ? 'stacked' : 'hidden')">
		<div class="mb-4 flex flex-wrap items-start justify-between gap-3">
			<div class="min-w-0 flex-1">
				<h2 class="mb-0.5 text-headline font-semibold text-surface-text-2">Theme &amp; settings</h2>
				<p class="mb-0 text-tiny text-surface-7">
					<template v-if="isBroadcast">Branding and behaviour of the site. Service times, About and Giving live under <router-link :to="{ name: 'SiteBroadcast', params: { siteId } }" class="underline" data-testid="theme-broadcast-link">Settings › Broadcast</router-link>.{{ ' ' }}</template>
					<template v-else>Branding, layout and behaviour of the site itself.{{ ' ' }}</template>
					<template v-if="draftSupported">Changes are saved as a draft and go live when you publish.</template>
					<template v-else>Saved changes go live within a minute.</template>
				</p>
			</div>
		</div>

		<Alert v-if="loadError" type="error" class="mb-4">{{ loadError.message }}</Alert>
		<Spinner v-else-if="loading" text="Loading settings…" classes="text-surface-8 mt-24" spinner-color="var(--c-dark-8)" spinner-size="15px" />

		<div v-else :class="sideBySide ? 'grid grid-cols-[minmax(0,1fr)_clamp(480px,45%,640px)] items-start gap-5' : 'flex flex-col gap-4'">
			<form class="min-w-0" data-testid="theme-form" @submit.prevent="save">
				<TabGroup type="segmented" class="mb-4" custom-classes="overflow-x-auto no-scrollbar" data-testid="theme-subtabs">
					<TabItem v-for="s in sections" :key="s.id" :is-active="section === s.id" :data-testid="`theme-tab-${s.id}`" @click="goSection(s.id)">
						{{ s.label }}
						<span v-if="changeCount(s.id)" class="ml-1.5 size-1.5 rounded-full" :class="sectionErrors[s.id] ? 'bg-error' : 'bg-gold'" :title="`${changeCount(s.id)} unsaved`" aria-hidden="true"></span>
					</TabItem>
				</TabGroup>

				<p class="mb-3 text-tiny text-surface-7" data-testid="theme-section-description">{{ sectionMeta.description }}</p>

				<!-- Branding -->
				<section v-if="section === 'branding'" class="rounded-lg bg-surface-2 p-4 sm:p-5" data-testid="theme-section-branding">
					<p v-if="seeded.length" class="mb-4 text-tiny text-surface-7">Fields marked “from Castr” have not been set here yet and still use the brand seed the site was created with.</p>
					<div class="flex flex-col gap-4">
						<div data-testid="theme-site-name">
							<p class="mb-1 text-tiny font-medium text-surface-text-1">Site name</p>
							<p class="mb-0 flex flex-wrap items-baseline gap-x-2 text-sm text-surface-8">
								<span class="truncate text-surface-text-2">{{ site.name }}</span>
								<router-link :to="{ name: 'SiteGeneral', params: { siteId } }" class="text-tiny text-main-light underline-offset-2 hover:underline" data-testid="theme-site-name-edit">Edit in Settings › General</router-link>
							</p>
						</div>
						<SiteImageField id="theme-logo" v-model="form.websiteLogo" :site-id="siteId" :label="`Logo${seeded.includes('websiteLogo') ? ' (from Castr)' : ''}`"
							:hint="LOGO_HINT" :recommended-max-bytes="ONE_MB" preview="logo" />
						<SiteImageField id="theme-favicon" v-model="form.websiteFavicon" :site-id="siteId" :label="`Favicon${seeded.includes('websiteFavicon') ? ' (from Castr)' : ''}`"
							:hint="FAVICON_HINT" preview="square" />
						<SiteFontSelect id="theme-font" ref="fontSelect" v-model="form.fontFamily" :label="`Font family${seeded.includes('fontFamily') ? ' (from Castr)' : ''}`"
							:seeded-value="seeded.includes('fontFamily') ? settings.fontFamily : ''" />

						<!-- Broadcast colour presets (contract §2.3) -->
						<div v-if="isBroadcast" data-testid="theme-broadcast-presets">
							<p class="mb-0.5 text-tiny font-medium text-surface-text-1">Colour presets</p>
							<p class="mb-2 text-tiny text-surface-7">Looks made for the Broadcast template, each with a matching light or dark chat. Pick the chat appearance on the stream under Castr › Live streams › Audience.</p>
							<div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
								<button v-for="preset in broadcastPresets" :key="preset.id" type="button"
									class="flex min-w-0 items-start gap-3 rounded-lg border p-3 text-left transition-colors"
									:class="preset.active ? 'border-main bg-main-transparent' : 'border-surface-4 bg-surface-3 hover:border-surface-6'"
									:aria-pressed="preset.active" :data-testid="`theme-preset-${preset.id}`" @click="applyBroadcastPreset(preset)">
									<span class="relative size-12 shrink-0 overflow-hidden rounded-md ring-1 ring-surface-4" :style="{ backgroundColor: preset.colors.background }" aria-hidden="true">
										<span class="absolute left-1.5 top-2 h-1 w-6 rounded-sm" :style="{ backgroundColor: preset.colors.text }" />
										<span class="absolute left-1.5 top-4 h-1 w-4 rounded-sm opacity-60" :style="{ backgroundColor: preset.colors.text }" />
										<span class="absolute bottom-1.5 right-1.5 h-2.5 w-5 rounded-sm" :style="{ backgroundColor: preset.colors.primary }" />
									</span>
									<span class="min-w-0 flex-1">
										<span class="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-medium text-white">
											<span>{{ preset.title }}</span>
											<span v-if="preset.active" class="shrink-0 whitespace-nowrap rounded-full bg-main px-1.5 py-px text-[10px] font-medium uppercase tracking-wide text-white" data-testid="theme-preset-applied">Applied</span>
										</span>
										<span class="mt-0.5 block text-tiny text-surface-7">{{ preset.description }}</span>
									</span>
								</button>
							</div>
						</div>

						<div>
							<div class="mb-2 flex flex-wrap items-center justify-between gap-2">
								<p class="mb-0 text-tiny font-medium text-surface-text-1">Colours</p>
								<button type="button" class="text-tiny text-surface-8 underline hover:text-white disabled:no-underline disabled:opacity-50" :disabled="allColorsDefault" data-testid="reset-all-colors" @click="resetAllColors">
									Reset all to defaults
								</button>
							</div>
							<!-- auto-fit: three across when there is room, wrapping as the form column narrows beside the preview -->
							<div class="grid grid-cols-[repeat(auto-fit,minmax(10.5rem,1fr))] gap-3">
								<div v-for="field in colorFields" :key="field.key" class="min-w-0">
									<div class="flex items-end gap-2">
										<FormInput :id="`theme-color-${field.key}`" v-model="form.colors[field.key]" :label="field.label" :placeholder="defaultColors[field.key]" :is-error="Boolean(colorErrors[field.key])" class="min-w-0 flex-1" />
										<label class="relative mb-px size-8 shrink-0 cursor-pointer overflow-hidden rounded ring-1 ring-surface-4" :title="`Pick ${field.label.toLowerCase()}`"
											:style="{ backgroundColor: HEX.test(form.colors[field.key] || '') ? form.colors[field.key] : 'transparent' }">
											<input type="color" class="absolute inset-0 size-full cursor-pointer opacity-0" :aria-label="`${field.label} picker`"
												:value="HEX.test(form.colors[field.key] || '') ? form.colors[field.key] : defaultColors[field.key]" @input="form.colors[field.key] = $event.target.value">
										</label>
									</div>
									<p v-if="colorErrors[field.key]" class="mb-0 mt-1 text-tiny text-error">{{ colorErrors[field.key] }}</p>
									<p v-else class="mb-0 mt-1 flex min-w-0 items-center gap-1.5 text-tiny text-surface-7">
										<span class="truncate">{{ field.hint }}</span>
										<button v-if="!isDefaultColor(field.key)" type="button" class="ml-auto shrink-0 whitespace-nowrap text-surface-8 underline hover:text-white"
											:title="`Default ${defaultColors[field.key]}`" :aria-label="`Reset ${field.label.toLowerCase()} to default`" @click="resetColor(field.key)">Reset</button>
										<span v-else class="ml-auto shrink-0 whitespace-nowrap text-surface-6">default</span>
									</p>
								</div>
							</div>
						</div>
					</div>
				</section>

				<!-- Texts: Broadcast page (v0.19) or catalog labels, then sign-in & sign-up (v0.20) -->
				<div v-else-if="section === 'texts'" class="flex flex-col gap-4" data-testid="theme-section-texts">
					<BroadcastTextsCard v-if="isBroadcast" v-model="form.broadcastTexts" :site-id="siteId" :audience="audience" />
					<SiteDisplaySettings v-else key="texts" v-model="displayDraft" :base="displayBase" :writable-keys="displayWritableKeys" :categories="categories" :site-id="siteId" :errors="displayErrs"
						:cards="['texts']" :ignored-keys="templateIgnoredKeys" :template-label="templateLabel" single-column />
					<SiteAuthTextsCard v-if="authTextsWritable" v-model="form.authTexts" :template="template" :settings="settings" />
				</div>

				<!-- Display cards: Layout (with presets) · Navigation & sections · Player -->
				<SiteDisplaySettings v-else-if="sectionMeta.card" :key="sectionMeta.card" v-model="displayDraft" :base="displayBase" :writable-keys="writableKeys" :categories="categories" :site-id="siteId" :errors="displayErrs"
					:cards="[sectionMeta.card]" :show-presets="sectionMeta.card === 'layout'" :ignored-keys="templateIgnoredKeys" :template-label="templateLabel" single-column />

				<!-- Access & accounts -->
				<div v-else-if="section === 'access'" class="flex flex-col gap-4" data-testid="theme-section-access">
					<section class="rounded-lg bg-surface-2 p-4 sm:p-5" data-testid="theme-card-registration">
						<h3 class="mb-1 text-sm font-semibold text-white">Registration</h3>
						<p class="mb-3 text-tiny text-surface-7">Whether new viewers can register on the site. Who can watch each item is set in the Library (Public, Login required or Paid).</p>
						<FormSwitch id="theme-registration" v-model="form.registrationEnabled" :label="form.registrationEnabled ? 'Registration open' : 'Registration closed (existing viewers can still sign in)'" />
						<p class="mb-0 mt-2 text-tiny text-surface-7">When closed, the site hides the register button and refuses new sign-ups.</p>
					</section>

					<section class="rounded-lg bg-surface-2 p-4 sm:p-5" data-testid="theme-card-legal">
						<h3 class="mb-1 text-sm font-semibold text-white">Legal &amp; support</h3>
						<p class="mb-4 text-tiny text-surface-7">Registration shows the “I agree to the Terms of Service / Privacy Policy” step only when at least one of these links is set.</p>
						<template v-if="legalSupported">
							<div v-for="field in LEGAL_FIELDS" :key="field.key" class="mb-4 min-w-0">
								<FormInput v-if="legalKeyWritable(field.key)" :id="`theme-${field.key}`" v-model="form[field.key]" :label="field.label" placeholder="https://… or /terms" input-classes="placeholder:text-surface-6" :is-error="Boolean(legalErrors[field.key])" class="min-w-0" />
								<div v-if="legalKeyWritable(field.key)" class="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
									<template v-if="pageLinkOptions.length > 1">
										<label :for="`theme-${field.key}-page`" class="sr-only">{{ field.label }} — use a site page</label>
										<FormSelect :id="`theme-${field.key}-page`" model-value="" :options="pageLinkOptions" size="sm" class="!w-52" @update:model-value="pickPage(field.key, $event)" />
									</template>
									<p class="mb-0 text-tiny" :class="legalErrors[field.key] ? 'text-error' : 'text-surface-7'">{{ legalErrors[field.key] || (pageLinkOptions.length > 1 ? field.help : `${field.help} Add pages under Content › Pages to pick one here.`) }}</p>
								</div>
							</div>
						</template>
						<p v-else class="mb-4 rounded-md bg-surface-3 px-3 py-2 text-tiny text-surface-7" data-testid="theme-legal-unsupported">
							Terms of Service and Privacy Policy links need a newer site runtime — they appear here as soon as your site is updated.
						</p>
						<template v-if="supportEmailWritable">
							<FormInput id="theme-support-email" v-model="form.supportEmail" type="email" label="Support email" placeholder="support@example.com" input-classes="placeholder:text-surface-6" :maxlength="191" :is-error="Boolean(legalErrors.supportEmail)" class="min-w-0" />
							<p class="mb-0 mt-1.5 text-tiny" :class="legalErrors.supportEmail ? 'text-error' : 'text-surface-7'">{{ legalErrors.supportEmail || 'Shown on viewers’ Account page and used as the reply-to address on password reset emails.' }}</p>
						</template>
					</section>
				</div>

				<!-- Footer -->
				<section v-else-if="section === 'footer'" class="rounded-lg bg-surface-2 p-4 sm:p-5" data-testid="theme-section-footer">
					<p class="mb-4 text-tiny text-surface-7">Static pages appear in the footer automatically.</p>
					<FormInput id="theme-footer-text" v-model="form.websiteFooterText" label="Footer text" :placeholder="`e.g. © ${site.name}`" input-classes="placeholder:text-surface-6" />
					<div class="mt-4 flex flex-col gap-2">
						<p class="mb-0 text-tiny font-medium text-surface-8">Links</p>
						<div v-for="(link, i) in form.footerLinks" :key="`fl-${i}`" class="min-w-0" data-testid="footer-link-row">
							<div class="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-end">
								<FormInput :id="`footer-link-label-${i}`" v-model="link.label" label="Label" placeholder="e.g. Contact" input-classes="placeholder:text-surface-6" :maxlength="80" class="min-w-0 sm:w-40 sm:shrink-0" />
								<FormInput :id="`footer-link-url-${i}`" v-model="link.url" label="URL" placeholder="e.g. https://… or /about" input-classes="placeholder:text-surface-6" class="min-w-0 flex-1" :is-error="Boolean(footerLinkErrors[i])" />
								<FormButton is-icon type="danger" size="md" title="Remove link" class="mb-px self-end" @click="removeFooterLink(i)">
									<icon-base :width="16" :height="16" fill="none"><icon-bin /></icon-base>
								</FormButton>
							</div>
							<p v-if="footerLinkErrors[i]" class="mb-0 mt-1 text-tiny text-error">{{ footerLinkErrors[i] }}</p>
						</div>
						<FormButton type="secondary" size="sm" class="self-start" :disabled="form.footerLinks.length >= MAX_FOOTER_LINKS" @click="addFooterLink">
							<icon-base class="mr-1" :width="12" :height="12"><icon-plus /></icon-base>
							Add link
						</FormButton>
					</div>
					<div class="mt-4 flex flex-col gap-2">
						<p class="mb-0 text-tiny font-medium text-surface-8">Social &amp; app-store links</p>
						<div v-for="(link, i) in form.socialLinks" :key="i" class="min-w-0">
							<div class="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-end">
								<FormSelect :id="`social-link-platform-${i}`" v-model="link.platform" label="Platform" :options="platformOptions" class="min-w-0 sm:w-40 sm:shrink-0" />
								<FormInput :id="`social-link-url-${i}`" v-model="link.url" label="URL" placeholder="e.g. https://…" input-classes="placeholder:text-surface-6" class="min-w-0 flex-1" :is-error="Boolean(linkErrors[i])" />
								<FormButton is-icon type="danger" size="md" title="Remove link" class="mb-px self-end" @click="removeLink(i)">
									<icon-base :width="16" :height="16" fill="none"><icon-bin /></icon-base>
								</FormButton>
							</div>
							<p v-if="linkErrors[i]" class="mb-0 mt-1 text-tiny text-error">{{ linkErrors[i] }}</p>
						</div>
						<div class="flex flex-wrap items-center gap-3">
							<FormButton type="secondary" size="sm" :disabled="form.socialLinks.length >= PLATFORMS.length" @click="addLink">
								<icon-base class="mr-1" :width="12" :height="12"><icon-plus /></icon-base>
								Add social link
							</FormButton>
							<FormSwitch id="theme-social-public" v-model="form.isSocialPublic" :label="form.isSocialPublic ? 'Links visible' : 'Links hidden'" />
						</div>
					</div>
				</section>

				<!-- Sticky save bar: unsaved (this browser) vs unpublished (runtime draft) -->
				<div class="sticky bottom-0 z-10 -mx-4 mt-4 flex flex-wrap items-center gap-3 border-t border-surface-3 bg-surface-1/95 px-4 py-3 backdrop-blur lg:mx-0 lg:rounded-lg lg:border lg:px-5" data-testid="theme-save-bar">
					<p class="mb-0 mr-auto flex min-w-0 flex-wrap items-center gap-x-2 text-tiny" data-testid="display-change-count">
						<template v-if="unsavedCount">
							<span class="size-2 shrink-0 rounded-full bg-gold" aria-hidden="true"></span>
							<span class="text-white">{{ unsavedCount }} unsaved {{ unsavedCount === 1 ? 'change' : 'changes' }}</span>
							<span class="text-surface-7">in
								<template v-for="(s, i) in dirtySections" :key="s.id"><button type="button" class="underline hover:text-white" @click="goSection(s.id)">{{ s.label }}</button><template v-if="i < dirtySections.length - 1">, </template></template>
							</span>
							<span v-if="displayChangeCount" class="text-surface-7" data-testid="display-change-count-detail">· {{ displayChangeCount }} display {{ displayChangeCount === 1 ? 'option' : 'options' }} changed</span>
							<button type="button" class="text-surface-8 underline hover:text-white" data-testid="theme-discard-unsaved" @click="discardUnsaved">Discard</button>
						</template>
						<template v-else-if="draftSupported && unpublishedCount">
							<span class="size-2 shrink-0 rounded-full bg-main" aria-hidden="true"></span>
							<span class="text-white">{{ unpublishedCount }} unpublished {{ unpublishedCount === 1 ? 'change' : 'changes' }}</span>
							<span class="text-surface-7">— viewers still see the published version.</span>
						</template>
						<template v-else>
							<span class="size-2 shrink-0 rounded-full bg-success" aria-hidden="true"></span>
							<span class="text-surface-7">{{ draftSupported ? 'Everything is published.' : 'All changes saved.' }}</span>
						</template>
					</p>
					<FormButton v-if="!unsavedCount && draftSupported && unpublishedCount" type="main" size="md" class="w-full justify-center sm:w-auto" :is-loading="publishing" :disabled="publishing" data-testid="theme-publish" @click="publish">
						Publish changes
					</FormButton>
					<FormButton v-else type="main" size="md" class="w-full justify-center sm:w-auto" :disabled="!valid || saving || !unsavedCount" :is-loading="saving" data-testid="theme-save" @click="save">
						{{ saveLabel }}
					</FormButton>
				</div>
			</form>

			<!-- Live preview beside the form (wide content column) -->
			<div v-if="sideBySide" class="sticky top-6 min-w-0">
				<SitePreviewPane :site-id="siteId" :site="site" :version="previewVersion" />
			</div>
			<!-- Narrower: full-width collapsible section under the form -->
			<section v-else-if="mdUp" class="flex min-w-0 flex-col gap-2" data-testid="theme-preview-section">
				<button type="button" class="flex w-full items-center justify-between gap-3 rounded-lg bg-surface-2 px-4 py-2.5 text-left transition-colors hover:bg-surface-3"
					:aria-expanded="previewShown" data-testid="theme-preview-toggle" @click="togglePreview">
					<span class="flex items-center gap-2 text-sm font-medium text-white">
						<icon-base :width="16" :height="16" :width-view-box="24" :height-view-box="24" fill="none"><icon-eye-outline /></icon-base>
						Live preview
					</span>
					<span class="text-tiny text-surface-7">{{ previewShown ? 'Hide preview' : 'Show preview' }}</span>
				</button>
				<SitePreviewPane v-if="previewShown" :site-id="siteId" :site="site" :version="previewVersion" :max-height="640" />
			</section>
		</div>
	</div>
</template>
